import React, { PropsWithChildren, useMemo } from 'react';
import { CollapsibleTreeItems, SidebarTreeItem } from 'src/components/widgets/sidebar/collapsibleTreeItems';
import { SidebarButtons, SidebarListItem } from 'src/components/widgets/sidebar/commonStyleSideBar';
import { TreeSidebarIcon } from 'src/components/widgets/sidebar/treeSideBar';
import styled from 'styled-components';
import { GhostButton } from '../../widgets/buttons';
import { DropdownActionItem, DropdownActions } from '../../widgets/dropdown';
import { Icon } from '../../widgets/icon';
import { SidebarButton, SidebarItemIdType } from '../../widgets/layouts';
import { ZTooltip } from '../../widgets/ztooltip';
import { SidebarPlacementItem } from './placementAreaDesigner';
import MoreVertIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import { T } from 'src/shared/helpers';

interface PlacementSidebarProps<T extends SidebarItemIdType = string> {
    sidebarHeader?: string;
    sidebarHeaderButtons?: SidebarButton[];
    sidebarItems: SidebarPlacementItem<string>[];
    onSidebarItemClick: (item: SidebarPlacementItem<string>) => void;
    onSidebarItemButtonClick?: (button: SidebarButton, item: SidebarPlacementItem<string>) => void;
}

const ListItem = styled(SidebarListItem)`
    min-height: 32px;
    padding: 2px 2px 2px 10px;
    cursor: pointer;
    max-width: none;
`;

const PlacementSidebarContainer = styled.div`
    background-color: white;
    padding: 0px 0px;
    overflow: auto;
`;

const DropdownActionItemContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const DropdownActionItemIcon = styled(Icon)<{ $isActive?: boolean }>`
    width: 14px;
    height: 14px;
    flex-shrink: 0;

    ${(props) =>
        props.$isActive &&
        `
        color: ${props.theme.app.secondary};
    `}
`;

const treeItemsStyle = {
    padding: '0'
};

const renderSingleButton = (
    button: SidebarButton,
    item: SidebarPlacementItem<string>,
    onClick?: (button: SidebarButton, item: SidebarPlacementItem<string>) => void
) => {
    const buttonElement = (
        <GhostButton
            $size='small'
            $type='icon'
            key={button.key}
            onClick={(e) => {
                e.stopPropagation();
                onClick?.(button, item);
            }}
        >
            <TreeSidebarIcon $isActive={button.isActive}>{button.icon}</TreeSidebarIcon>
        </GhostButton>
    );

    return button.tooltip ? (
        <ZTooltip key={button.key} position='top' title={button.tooltip}>
            {buttonElement}
        </ZTooltip>
    ) : (
        buttonElement
    );
};

const renderButtons = (
    buttons: SidebarButton[],
    item: SidebarPlacementItem<string>,
    onClick?: (button: SidebarButton, item: SidebarPlacementItem<string>) => void
) => {
    if (!buttons || buttons.length === 0) return null;

    if (buttons.length === 1) return renderSingleButton(buttons[0], item, onClick);

    return (
        <DropdownActions
            header={
                <GhostButton $size='small' $type='icon' aria-label={T._('Actions', 'Admin')}>
                    <Icon>
                        <MoreVertIcon />
                    </Icon>
                </GhostButton>
            }
            items={buttons.map((button) => (
                <DropdownActionItem key={button.key} onClick={() => onClick?.(button, item)}>
                    <DropdownActionItemContent>
                        <DropdownActionItemIcon $isActive={button.isActive}>{button.icon}</DropdownActionItemIcon>
                        {button.tooltip}
                    </DropdownActionItemContent>
                </DropdownActionItem>
            ))}
            orientation='RIGHT'
        />
    );
};

export const PlacementTreeSidebar = <T extends SidebarItemIdType = string>({
    sidebarItems,
    onSidebarItemClick,
    onSidebarItemButtonClick
}: PropsWithChildren<PlacementSidebarProps<T>>) => {
    const items: SidebarTreeItem[] = useMemo(
        () =>
            sidebarItems.map((x) => {
                const firstLevel: SidebarTreeItem[] =
                    x.subItems == null
                        ? []
                        : x.subItems.map((c) => {
                              const secondLevel =
                                  c.subItems == null
                                      ? []
                                      : c.subItems.map((y, i) => {
                                            const element = (
                                                <ListItem key={y.id.toString()} onClick={() => onSidebarItemClick(y)}>
                                                    <span>{y.label}</span>
                                                    <SidebarButtons>{y.buttons && renderButtons(y.buttons, y, onSidebarItemButtonClick)}</SidebarButtons>
                                                </ListItem>
                                            );

                                            return {
                                                id: y?.id,
                                                content: element,
                                                selected: y.selected
                                            };
                                        });

                              let secondNode = (
                                  <ListItem key={c.id.toString()} onClick={() => onSidebarItemClick(c)}>
                                      <span>{c.label}</span>
                                      <SidebarButtons>{c.buttons && renderButtons(c.buttons, c, onSidebarItemButtonClick)}</SidebarButtons>
                                  </ListItem>
                              );

                              return {
                                  id: c?.id,
                                  content: secondNode,
                                  items: secondLevel,
                                  opened: sidebarItems.length === 1 || c.added || c.subItems?.some((i) => i.added),
                                  notOpenOnClick: false
                              };
                          });

                const firstNode = (
                    <ListItem
                        onClick={(e) => {
                            onSidebarItemClick(x);
                        }}
                    >
                        <span>{x.label}</span>
                        <SidebarButtons>{x.buttons && renderButtons(x.buttons, x, onSidebarItemButtonClick)}</SidebarButtons>
                    </ListItem>
                );

                return {
                    id: x?.id,
                    content: firstNode,
                    items: firstLevel,
                    opened:
                        sidebarItems.length === 1 || x.added || x.subItems?.some((i) => i.added) || x.subItems?.some((i) => i.subItems?.some((y) => y.added)),
                    notOpenOnClick: false
                };
            }),
        [sidebarItems]
    );

    return (
        <PlacementSidebarContainer>
            <CollapsibleTreeItems items={items} customStyle={treeItemsStyle} />
        </PlacementSidebarContainer>
    );
};
